import ArrowsIcon from 'assets/svg/icons/Arrows';
import AlertV2 from 'components/atoms/AlertV2/AlertV2.component';
import useUpdateDocument from 'hooks/DocumentsRepository/useUpdateDocument';
import useUpdateDocumentField from 'hooks/DocumentsRepository/useUpdateDocumentField';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router';
import type { DocumentReferenceFieldTax } from 'types/repository.types';
import formatPeriodLabel from 'utils/formatPeriodLabel';
import useDocumentFields from './useDocumentFields';
import useTaxLink from './useTaxLink';

type DocumentRepositoryTaxInfoProps = {
  documentId: string;
};

const DocumentRepositoryTaxInfo = ({
  documentId,
}: DocumentRepositoryTaxInfoProps) => {
  const { data: documentFields } = useDocumentFields(documentId);

  const { t } = useTranslation();

  const taxField = documentFields?.find(
    ({ type, value, status }) =>
      type === 'tax' &&
      status === 'accepted' &&
      typeof value === 'object' &&
      value !== null &&
      !!value.period &&
      !!value.type,
  ) as DocumentReferenceFieldTax;

  const { link: taxLink } = useTaxLink(taxField);
  const navigate = useNavigate();

  const primaryAction = useMemo(() => {
    if (!taxLink) return undefined;

    return {
      label: t('repository.document_form.fields.tax.primaryAction'),
      onClick: () => navigate(taxLink),
    };
  }, [taxLink, navigate, t]);

  const { mutate: updateDocumentField } = useUpdateDocumentField(documentId);
  const { mutate: updateDocument } = useUpdateDocument();

  const onDismiss = useCallback(() => {
    updateDocumentField({ ...taxField, status: 'rejected' });
    updateDocument({ documentId, taxId: undefined });
  }, [updateDocumentField, updateDocument, documentId, taxField]);

  if (!taxField) return null;

  return (
    <AlertV2
      variant="filled"
      type="information"
      size="small"
      icon={ArrowsIcon}
      className="pr-3"
      dismissible
      onDismiss={onDismiss}
      description={
        <ReactMarkdown className="text-blue-900">
          {t(`repository.document_form.fields.tax.description`, {
            taxType: t(`taxes.filters.types.${taxField.value.type}`),
            periodString: formatPeriodLabel(taxField.value.period),
          })}
        </ReactMarkdown>
      }
      primaryAction={primaryAction}
    />
  );
};

export default DocumentRepositoryTaxInfo;
